/**
 * 🚨 系統事件 TAB（migration v27，2026-09-11 Captain 指示「ensure to have system log
 * so that we can fix any error easily」）——自動採集嘅系統事件：cron 失敗／出站發送
 * 失敗／LLM 硬失敗。30 日保留（hourly purge）。人工上線紀錄喺「📋 更新日誌」TAB。
 */

import { useEffect, useState } from "react";
import { listSystemLogs, type SystemLogRow } from "../lib/api";

const LEVEL_STYLE: Record<string, string> = {
  error: "bg-red-500/15 text-red-400 border-red-500/40",
  warn: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  info: "bg-sky-500/15 text-sky-400 border-sky-500/40",
};

const LEVEL_ICON: Record<string, string> = { error: "🚨", warn: "⚠️", info: "ℹ️" };

function fmtTime(ts: number): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export default function SystemLogTab() {
  const [rows, setRows] = useState<SystemLogRow[]>([]);
  const [level, setLevel] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await listSystemLogs({ level: level || undefined, q: q || undefined, limit: 150 });
      setRows(r.logs || []);
      setFetchedAt(new Date());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {[
            { v: "", label: "全部" },
            { v: "error", label: "🚨 錯誤" },
            { v: "warn", label: "⚠️ 警告" },
            { v: "info", label: "ℹ️ 資訊" },
          ].map((o) => (
            <button
              key={o.v}
              onClick={() => setLevel(o.v)}
              className={`px-3 py-1.5 rounded-lg text-sm border transition ${
                level === o.v
                  ? "bg-white/10 border-white/30 text-white"
                  : "border-white/10 text-white/60 hover:text-white"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="搜尋事件／詳情（例如 send_failed）"
          className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30 w-72"
        />
        <button
          onClick={load}
          disabled={loading}
          className="px-3 py-1.5 rounded-lg text-sm bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-50"
        >
          {loading ? "載入中…" : "🔄 更新"}
        </button>
        <span className="text-xs text-white/40">
          {fetchedAt ? `最後更新 ${fmtTime(fetchedAt.getTime())} · 30 日保留` : ""}
        </span>
      </div>

      {error && (
        <div className="text-sm text-red-400 border border-red-500/30 rounded-lg px-4 py-3 bg-red-500/10">
          載入失敗：{error}
        </div>
      )}

      {!error && rows.length === 0 && !loading && (
        <div className="text-sm text-white/40 border border-white/10 rounded-lg px-4 py-8 text-center">
          沒有符合的系統事件——好事 🎉（有錯誤時會喺度出現：cron 失敗／發送失敗／AI 硬失敗）
        </div>
      )}

      {rows.length > 0 && (
        <div className="border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/50 text-left">
              <tr>
                <th className="px-4 py-2.5 font-medium">時間</th>
                <th className="px-3 py-2.5 font-medium">級別</th>
                <th className="px-3 py-2.5 font-medium">來源</th>
                <th className="px-3 py-2.5 font-medium">事件</th>
                <th className="px-3 py-2.5 font-medium">租戶</th>
                <th className="px-4 py-2.5 font-medium">詳情</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                  <td className="px-4 py-2.5 text-white/70 font-mono text-xs whitespace-nowrap">{fmtTime(r.ts)}</td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 rounded border text-xs whitespace-nowrap ${LEVEL_STYLE[r.level] || "border-white/20 text-white/60"}`}>
                      {LEVEL_ICON[r.level] || ""} {r.level}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-white/60 text-xs">{r.source}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-white">{r.event}</td>
                  <td className="px-3 py-2.5 text-white/60 text-xs">{r.tenant_id || "—"}</td>
                  <td className="px-4 py-2.5 text-white/50 text-xs break-all max-w-md">{r.detail || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
